import React, { Suspense, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SideBar from "./SideBar";
import PresenceHeartbeat from "./PresenceHeartbeat";
import { SidebarCollapseProvider } from "../Contexts/SidebarCollapseContext";
import styles from "./AppLayout.module.scss";

/**
 * Authenticated shell: sidebar + main content. No top navbar (English Dept CRM pattern).
 */
export default function AppLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const isReportBuilderPage =
    pathname === "/reports" || pathname.endsWith("/reports");
  const hideFooter =
    pathname === "/home" ||
    pathname.endsWith("/home") ||
    pathname === "/all-enquiry" ||
    pathname.endsWith("/all-enquiry") ||
    pathname === "/users" ||
    pathname.endsWith("/users") ||
    pathname === "/new-enquiry" ||
    pathname.endsWith("/new-enquiry") ||
    pathname === "/exhibition-enquiries" ||
    pathname.endsWith("/exhibition-enquiries") ||
    pathname === "/view-applications" ||
    pathname.endsWith("/view-applications") ||
    pathname === "/application-form-issue" ||
    pathname.endsWith("/application-form-issue") ||
    pathname === "/counsellor-applications" ||
    pathname.endsWith("/counsellor-applications") ||
    pathname === "/universities/list" ||
    pathname.endsWith("/universities/list") ||
    pathname === "/universities/approval" ||
    pathname.endsWith("/universities/approval") ||
    pathname === "/marketing-overview" ||
    pathname.endsWith("/marketing-overview") ||
    pathname === "/assign-visa-counsellor" ||
    pathname.endsWith("/assign-visa-counsellor") ||
    pathname === "/appointments/my" ||
    pathname.endsWith("/appointments/my") ||
    pathname === "/appointments/all" ||
    pathname.endsWith("/appointments/all") ||
    pathname === "/server-logs" ||
    pathname.endsWith("/server-logs");

  return (
    <SidebarCollapseProvider>
      <PresenceHeartbeat />
      <Box className={`${styles.root} app-layout-root`}>
        {isMobile && (
          <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            classes={{ paper: styles.drawerPaper }}
          >
            <Box className={styles.drawerHeader}>
              <IconButton onClick={() => setSidebarOpen(false)} aria-label="Close menu">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box className={styles.drawerSidebar}>
              <SideBar />
            </Box>
          </Drawer>
        )}

        <Grid container spacing={0} className={styles.gridContainer}>
          {!isMobile && (
            <Grid
              item
              xs={12}
              md={2}
              className={`${styles.sidebarGrid} sidebar-column app-sidebar-host`}
            >
              <div className="sidebar-wrapper">
                <SideBar />
              </div>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            md={isMobile ? 12 : 10}
            className={`${styles.mainContent}${isMobile ? "" : " app-main-host"}`}
          >
            {isMobile && (
              <Box className={styles.mobileTopBar}>
                <IconButton
                  onClick={() => setSidebarOpen(true)}
                  edge="start"
                  aria-label="Open menu"
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
            <Box
              component="main"
              className={styles.outletWrapper}
              sx={
                !isMobile && isReportBuilderPage ? { paddingRight: 0 } : undefined
              }
            >
              <div className={styles.outletBody}>
                <Suspense
                  fallback={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 200,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  }
                >
                  <Outlet />
                </Suspense>
              </div>
              {!hideFooter && (
                <footer className={styles.appFooter}>
                  <p>© {new Date().getFullYear()} CrunchTime</p>
                </footer>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </SidebarCollapseProvider>
  );
}
